import { useRef, useState } from 'react';
import { store, useStoreRevision } from '../store';
import { Chart } from '../Chart';
import { formatDuration } from '../../engine/metrics.js';

export function ProgressView() {
  useStoreRevision();
  const attempts = store.attempts();
  const [message, setMessage] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const recent = attempts.slice(-30);
  const points = recent.map((a, index) => ({
    value: a.netCpm,
    label: `#${attempts.length - recent.length + index + 1}`,
  }));

  const weak = store.weakKeys(10);

  const download = () => {
    const blob = new Blob([store.export()], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pokrok-pisanie-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const importFile = async (file: File) => {
    try {
      store.import(await file.text());
      setMessage('Pokrok bol obnovený zo zálohy.');
    } catch (error) {
      setMessage(`Zálohu sa nepodarilo načítať: ${(error as Error).message}`);
    }
  };

  return (
    <>
      <div className="card">
        <h2>Vývoj rýchlosti</h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          Čistá rýchlosť v úhozoch za minútu za posledných {recent.length} pokusov.
        </p>
        <Chart points={points} unit="úhozov/min" />
      </div>

      <div className="grid-2">
        <div className="card">
          <h2>Najčastejšie chyby</h2>
          {weak.length === 0 ? (
            <p className="muted small">Zatiaľ žiadne chyby. Páni.</p>
          ) : (
            <div className="row">
              {weak.map((w) => (
                <span className="chip" key={w.char}>
                  <span className="k">{w.char === ' ' ? 'medzerník' : w.char}</span> {w.errors} chýb
                </span>
              ))}
            </div>
          )}
          <h3>Odporúčanie</h3>
          <p className="muted small" style={{ marginBottom: 0 }}>
            {weak.length === 0
              ? 'Prejdi na ďalší blok lekcií a skús rýchlostné drily.'
              : `Zameraj sa na klávesy ${weak.slice(0, 3).map((w) => `„${w.char}“`).join(', ')} — otvor blok, kde sa učia, a zopakuj ho.`}
          </p>
        </div>

        <div className="card">
          <h2>Záloha pokroku</h2>
          <p className="muted small" style={{ marginTop: 0 }}>
            Pokrok je uložený len v tomto prehliadači. Zálohou si ho prenesieš na iný počítač —
            namiesto účtov, ktoré by vyžadovali server a cookies.
          </p>
          <div className="row">
            <button type="button" className="btn" onClick={download}>Stiahnuť zálohu</button>
            <button type="button" className="btn" onClick={() => fileRef.current?.click()}>Obnoviť zo súboru</button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              className="visually-hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void importFile(file);
                event.target.value = '';
              }}
            />
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                if (window.confirm('Naozaj vymazať celý pokrok? Toto sa nedá vrátiť.')) {
                  store.reset();
                  setMessage('Pokrok bol vymazaný.');
                }
              }}
            >
              Vymazať pokrok
            </button>
          </div>
          {message && <p className="small" style={{ marginBottom: 0 }}>{message}</p>}
        </div>
      </div>

      <div className="card">
        <h2>Posledné pokusy</h2>
        {attempts.length === 0 ? (
          <p className="muted small">Zatiaľ nič. <a href="#/lekcie">Začni prvou lekciou</a>.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Dátum</th>
                <th>Lekcia</th>
                <th className="num">Čistá</th>
                <th className="num">Presnosť</th>
                <th className="num">Chyby</th>
                <th className="num">Čas</th>
              </tr>
            </thead>
            <tbody>
              {[...attempts].reverse().slice(0, 20).map((a, index) => (
                <tr key={`${a.finishedAt}-${index}`}>
                  <td>{new Date(a.finishedAt).toLocaleString('sk-SK')}</td>
                  <td>{a.lessonId === 'custom' ? 'Vlastný text' : (a.lessonTitle ?? '—')}</td>
                  <td className="num">{a.netCpm}</td>
                  <td className="num">{Math.round(a.accuracy * 100)} %</td>
                  <td className="num">{a.errors}</td>
                  <td className="num">{formatDuration(a.durationMs)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
